export type AnyObjectOrArray = Record<string, unknown> | unknown[];

/**
 * Checks if an object or array has at least one valid (non-empty, non-null, non-undefined, truthy) value.
 *
 * @template T - The type of the object or array.
 * @param {T} data - The object or array to check.
 * @returns {boolean} - Returns `true` if the object or array contains at least one valid value, otherwise `false`.
 */
const hasValidValues = <T extends AnyObjectOrArray>(data: T): boolean => {
  // Handle null/undefined input
  if (data === null || data === undefined) {
    return false;
  }

  return Object.values(data)?.some(value => {
    // Check for basic invalid values
    if (
      value === '' ||
      value === null ||
      value === undefined ||
      value === 0 ||
      value === false
    ) {
      return false;
    }
    // Check for empty objects and arrays
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (value !== null) {
        return Object.keys(value).length > 0;
      }
    }
    // All other truthy values are considered valid
    return Boolean(value);
  });
};

export default hasValidValues;
